import { Link, useParams, useSearchParams } from 'react-router-dom';
import bdseLogoUrl from '../assets/bdse-logo.jpg';

/**
 * Placeholder for the in-app assignment magic-link recipient flows.
 *
 * Routes reserved by Deliverable 7.1:
 *   /s/:token             - Signal assignment recipient view (rebuild in 7.2)
 *   /rfp-view?token=...   - RFP assignment/approval recipient view (rebuild in 7.3)
 *
 * These routes are claimed now to:
 *   1. Prevent any other feature from squatting them later.
 *   2. Make the parity commitment visible in the routing surface.
 *   3. Give a clean degradation path if a recipient clicks an old v0.7
 *      production link that points at the new domain before 7.2/7.3 ship.
 *
 * The recipient workflow MUST preserve v0.7 behavior exactly:
 *   - no login required
 *   - scoped view only (not full app shell)
 *   - all v0.7 actions available (notes, status, pass/reassign, contact lookup,
 *     research, draft outreach, comments, approval, completion)
 * Only the internals change: hashed tokens, Edge Function validation, audit log,
 * no privileged keys in browser. See docs/legacy_feature_parity_register.md.
 */
export function RecipientFlowComingSoon({
  variant,
}: {
  variant: 'signal' | 'rfp';
}) {
  const params = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const tokenPresent =
    variant === 'signal' ? Boolean(params.token) : Boolean(searchParams.get('token'));

  const headline =
    variant === 'signal' ? 'Signal assignment' : 'RFP assignment';

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="bdse-glass-card w-[480px] max-w-[92vw] px-10 pt-8 pb-7 text-center">
        <div className="-mt-1 -mb-2">
          <img
            src={bdseLogoUrl}
            alt="BD Signal Engine"
            className="mx-auto w-60 h-auto"
            style={{ mixBlendMode: 'multiply' }}
          />
        </div>

        <p className="bdse-tagline mb-4">{headline}</p>

        <div
          className="text-sm text-bdse-text-secondary leading-relaxed px-2 py-3 rounded-[10px] mb-4"
          style={{ background: 'rgba(42,125,140,0.04)', border: '1px solid rgba(42,125,140,0.12)' }}
        >
          {tokenPresent ? (
            <>
              This assignment link is recognized but the recipient view is being
              upgraded. Please contact the person who sent you this link.
            </>
          ) : (
            <>
              This page is reserved for assignment links. If you arrived here
              by mistake, please contact the person who shared the link.
            </>
          )}
        </div>

        <div className="mt-4 pt-3.5 border-t border-black/[0.06]">
          <p className="bdse-footnote">Ankura BD Intelligence Platform</p>
          <p className="bdse-footnote mt-1">
            <Link to="/login" className="text-bdse-teal hover:underline">
              Internal users sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
